import { useState } from 'react';

const checklistItems = [
  '評価基準が曖昧で判断がブレる',
  '頑張りが報われない制度感がある',
  '形骸化している人事制度がある',
  '職種ごとの要件・等級が不明確',
  '管理職登用ルール・育成体制が未整備',
  '後継者育成の仕組みが不在',
];

const flowItems = [
  { icon: 'ri-question-line', text: '評価基準が曖昧で上司の主観に依存', isArrow: false },
  { icon: '↓', text: '', isArrow: true },
  { icon: 'ri-emotion-unhappy-line', text: '社員のモチベーション・納得感が低下', isArrow: false },
  { icon: '↓', text: '', isArrow: true },
  { icon: 'ri-logout-box-r-line', text: '優秀な中堅社員が離職（競合他社へ流出）', isArrow: false },
  { icon: '↓', text: '', isArrow: true },
  { icon: 'ri-bar-chart-2-line', text: '組織力・採用力が低下、人件費が非効率化', isArrow: false },
  { icon: '↓', text: '', isArrow: true },
  { icon: 'ri-restart-line', text: '機能していない人事制度のまま負のスパイラルへ', isArrow: false },
];

const dataStats = [
  { value: '68%', label: 'の中小企業が人材確保の困難を課題に感じている' },
  { value: '50%', label: 'が評価制度への不満をモチベーション低下の要因と認識' },
  { value: '44%', label: 'が離職理由を把握できていない（制度がないため原因不明）' },
  { value: '75%', label: '30名以下の企業では人事制度が未整備の状況' },
];

export default function ProblemSection() {
  const [checked, setChecked] = useState<boolean[]>(new Array(checklistItems.length).fill(false));

  const toggleCheck = (index: number) => {
    setChecked((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const checkedCount = checked.filter(Boolean).length;

  return (
    <section
      id="problem"
      className="py-16"
      style={{ background: '#F7F9FC', fontFamily: "'Noto Sans JP', sans-serif" }}
    >
      <div className="max-w-[1080px] mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-2xl lg:text-[34px] font-bold text-[#1A1A2E] leading-tight">
            人事制度が機能しない組織に生じる『悪循環』
          </h2>
        </div>

        {/* Two Column Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          {/* Left: Checklist */}
          <div className="bg-white rounded-xl p-7 flex flex-col" style={{ boxShadow: '0 4px 24px rgba(13,43,85,0.10)' }}>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#EEF4FB] flex-shrink-0">
                <i className="ri-file-list-3-line text-[#1A5CB0] text-base"></i>
              </div>
              <h3 className="text-[#1A1A2E] text-base font-bold">貴社の人事課題チェックリスト</h3>
            </div>

            <ul className="flex flex-col justify-between flex-1 mb-5">
              {checklistItems.map((item, index) => (
                <li
                  key={index}
                  className="flex items-center gap-3 cursor-pointer group"
                  onClick={() => toggleCheck(index)}
                >
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                      checked[index]
                        ? 'bg-[#2D8EFF] border-[#2D8EFF]'
                        : 'border-[#D0DCF0] group-hover:border-[#2D8EFF]'
                    }`}
                  >
                    {checked[index] && <i className="ri-check-line text-white text-xs"></i>}
                  </div>
                  <span
                    className={`text-base leading-snug transition-colors ${
                      checked[index] ? 'text-[#2D8EFF] font-medium' : 'text-[#1A1A2E]'
                    }`}
                  >
                    {item}
                  </span>
                </li>
              ))}
            </ul>

            <div
              className={`rounded-lg px-4 py-3 transition-all ${
                checkedCount >= 2
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-[#EEF4FB] border border-[#D0DCF0]'
              }`}
            >
              {checkedCount >= 2 ? (
                <p className="text-center">
                  <span className="block text-[11px] text-red-400 font-medium mb-0.5">
                    チェックが{checkedCount}つ以上該当しています
                  </span>
                  <span className="text-red-600 font-extrabold text-[15px] tracking-wide leading-snug">
                    今すぐ診断を受けることをお勧めします
                  </span>
                </p>
              ) : (
                <p className="text-base font-extrabold text-[#E04040] text-center tracking-wide">
                  ２つ以上当てはまる場合は要見直し！
                </p>
              )}
            </div>
          </div>

          {/* Right: Vicious Cycle Flow */}
          <div className="bg-white rounded-xl p-7" style={{ boxShadow: '0 4px 24px rgba(13,43,85,0.10)' }}>
            <div className="flex items-center gap-2 mb-5">
              <span className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0"></span>
              <h3 className="text-[#1A1A2E] text-base font-bold">制度が機能しない時に起きる悪循環</h3>
            </div>

            <div className="flex flex-col gap-1">
              {flowItems.map((item, index) => (
                item.isArrow ? (
                  <div key={index} className="flex justify-center text-[#D0DCF0] text-xl font-light py-0.5">
                    ↓
                  </div>
                ) : (
                  <div
                    key={index}
                    className="flex items-center gap-3 bg-[#F7F9FC] rounded-lg px-4 py-3"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF4FB] flex-shrink-0">
                      <i className={`${item.icon} text-[#1A5CB0] text-base`}></i>
                    </div>
                    <span className="text-base text-[#1A1A2E] leading-snug">{item.text}</span>
                  </div>
                )
              ))}
            </div>
          </div>
        </div>

        {/* Data Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {dataStats.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 text-center"
              style={{ boxShadow: '0 4px 24px rgba(13,43,85,0.08)' }}
            >
              <span
                className="block font-bold text-[#0D2B55] leading-none mb-3"
                style={{ fontSize: '48px', letterSpacing: '-1px' }}
              >
                {stat.value}
              </span>
              <p className="text-[#667085] text-sm leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
